// interceptor/sentry-error.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SentryService } from '../../sentry.config';

@Injectable()
export class SentryErrorInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Add breadcrumb for outgoing requests
    SentryService.addBreadcrumb(`HTTP ${request.method} ${request.url}`, 'http', 'info');

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Capture HTTP errors in Sentry
        const errorContext = {
          request: {
            method: request.method,
            url: request.url,
            headers: this.sanitizeHeaders(request.headers),
          },
          response: {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            message: error.message,
          },
          timestamp: new Date().toISOString(),
        };

        // Add breadcrumb for the error
        SentryService.addBreadcrumb(
          `HTTP Error ${error.status} - ${request.method} ${request.url}`,
          'http.error',
          'error',
        );

        // Capture the error with context
        SentryService.captureException(
          new Error(`HTTP ${error.status}: ${error.message || error.statusText}`),
          errorContext,
        );

        // Set tags for better error grouping
        SentryService.setTag('http_status', error.status.toString());
        SentryService.setTag('http_method', request.method);
        SentryService.setTag('api_endpoint', this.extractEndpoint(request.url));

        return throwError(() => error);
      }),
    );
  }

  private sanitizeHeaders(headers: any): any {
    const sanitized: any = {};
    headers.keys().forEach((key: string) => {
      // Don't include sensitive headers
      if (!['authorization', 'cookie', 'x-api-key'].includes(key.toLowerCase())) {
        sanitized[key] = headers.get(key);
      } else {
        sanitized[key] = '[REDACTED]';
      }
    });
    return sanitized;
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.split('/').slice(0, 3).join('/') || '/';
    } catch {
      return url.split('?')[0] || url;
    }
  }
}
